import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { Cross } from "components/icons/Cross";
import { InfoSolid } from "components/InfoSolid";
import { YYYY_MM_DD, MM_DD_YYYY } from "constants/date";
import dayjs from "dayjs";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { editCategorisationRuleSchema } from "formValidations/categorisationRuleSchema";
import { AnimatePresence, motion } from "framer-motion";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import randomBytes from "randombytes";
import React, { ReactNode, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useApplyRuleMutation,
  useEditRuleMutation,
  useGetAllConditionsQuery,
  useGetRuleByIdQuery,
} from "store/apis/ruleEngine";
import { closeEditModal } from "store/slices/ruleEngine";
import { RootState } from "store/store";
import { ConditionResult } from "types/Models/ruleEngine";
import { BackendError } from "types/utils/error";
import {
  Actions,
  ConditionFunction,
  ConditionValue,
  RuleConsoleItems,
  RuleStepper,
  RuleValuesType,
  TransactionsPreview,
  TitleAndDuration,
} from "./CreateRule";
import { formatOrdinals } from "utils/formatOrdinals";
import { getAvailableRuleFields } from "utils/getAvailableRuleFields";
import { MAX_RULE_CONDITIONS } from "constants/ruleEngine";
import { Divider } from "components/design/Divider";
import ConditionalToolTip from "components/design/conditionalToolTip";

type EditRuleValuesType = {
  name: string;
  startDate: string;
  endDate: string;
  transactionsFor: string;
  assigned_category_id: string;
  apply_for_future_txns: boolean;
  apply_for_txns_with_no_category: boolean;
  apply_on_all_past_txns: boolean;
  assigned_vendor_id: string;
  classification_ids: string[];
  priority: string;
  ruleConditionData: {
    uuid: string;
    id: string;
    field: string;
    conditionFunction: string;
    conditionValue: string | number | string[];
  }[];
};

const initialRuleConditionData = {
  uuid: "",
  field: "",
  conditionFunction: "",
  conditionValue: "",
};

const ConditionWrapper = ({
  itemId,
  children,
}: {
  children: ({
    field,
    defaultConditionFunction,
    defaultField,
  }: {
    field: string | undefined;
    defaultField: ConditionResult | undefined;
    defaultConditionFunction:
      | {
          uuid: string;
          name: string;
          operator_type: string;
        }
      | undefined;
  }) => ReactNode;
  itemId: string;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { values } = useFormikContext<EditRuleValuesType>();
  const { ruleConditionData = [] } = values || {};

  const { field, conditionFunction } =
    ruleConditionData.find(({ id }) => id === itemId) || {};

  const { data: conditions = [] } = useGetAllConditionsQuery(
    {
      groupId,
      entityId,
    },
    { skip: !groupId || !entityId }
  );

  const defaultField = conditions.find(({ uuid }) => uuid === field);

  const defaultConditionFunction = defaultField?.available_operators?.find(
    ({ uuid }) => uuid === conditionFunction
  );

  return <>{children({ field, defaultConditionFunction, defaultField })}</>;
};

const Conditions = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { values, setFieldValue } = useFormikContext<RuleValuesType>();
  const { ruleConditionData = [] } = values || {};

  const {
    data: conditions = [],
    isLoading,
    isSuccess,
  } = useGetAllConditionsQuery(
    {
      groupId,
      entityId,
    },
    { skip: !groupId || !entityId }
  );

  const fieldOptionsByRow = useMemo(
    () =>
      ruleConditionData.map((_, rowIndex) =>
        getAvailableRuleFields({
          conditions,
          rows: ruleConditionData,
          rowIndex,
        })
      ),
    [conditions, ruleConditionData]
  );

  const onFieldChange = (index: number) => {
    setFieldValue(`ruleConditionData[${index}].conditionFunction`, "");
    setFieldValue(`ruleConditionData[${index}].conditionValue`, "");
  };

  const addNewCondition = () => {
    if (ruleConditionData.length < MAX_RULE_CONDITIONS) {
      const newValues = [
        ...values.ruleConditionData,
        {
          id: randomBytes(10).toString("hex"),
          ...initialRuleConditionData,
        },
      ];
      setFieldValue("ruleConditionData", newValues);
    }
  };

  const onRemove = (conditionId: string) => {
    if (ruleConditionData.length > 1) {
      const newValues = ruleConditionData.filter(({ id }) => {
        return id !== conditionId;
      });

      setFieldValue("ruleConditionData", newValues);
    }
  };

  if (isSuccess) {
    return (
      <RuleConsoleItems title="Define Conditions">
        <AnimatePresence>
          {ruleConditionData.map(({ id, conditionValue }, i) => {
            const defaultValue = Array.isArray(conditionValue)
              ? conditionValue.map((value) => ({
                  label: value,
                  value,
                }))
              : {
                  label: conditionValue,
                  value: conditionValue,
                };
            return (
              <motion.div
                className="t-flex t-gap-4"
                key={id}
                layout
                transition={{ duration: 0.1, ease: "easeOut" }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -500, opacity: 0 }}
              >
                <ConditionWrapper itemId={id}>
                  {({ field = "", defaultConditionFunction, defaultField }) => (
                    <div className="t-grid t-grid-cols-3 t-gap-4 t-w-full">
                      <Combobox
                        aria-label={`${formatOrdinals(i + 1)} Field`}
                        label={i === 0 ? "Field" : ""}
                        isDisabled={isLoading}
                        placeholder="Select"
                        menuPortalTarget={document.body}
                        name={`ruleConditionData[${i}].field`}
                        withForm
                        options={fieldOptionsByRow[i]}
                        isClearable={false}
                        block
                        required
                        onChange={() => onFieldChange(i)}
                        value={
                          Boolean(defaultField)
                            ? {
                                label: defaultField?.name,
                                value: defaultField?.uuid || "",
                              }
                            : null
                        }
                      />
                      <ConditionFunction
                        index={i}
                        field={field}
                        defaultValue={
                          Boolean(defaultConditionFunction)
                            ? {
                                label: (
                                  <span className="t-lowercase">
                                    {defaultConditionFunction?.name}
                                  </span>
                                ),
                                value: defaultConditionFunction?.uuid || "",
                              }
                            : null
                        }
                      />
                      <ConditionValue
                        index={i}
                        field={field}
                        defaultValue={
                          Boolean(conditionValue) &&
                          !(conditionValue instanceof Array)
                            ? defaultValue
                            : null
                        }
                      />
                    </div>
                  )}
                </ConditionWrapper>
                <div className="t-self-end">
                  <Button
                    customType="ghost_icon"
                    onClick={() => onRemove(id)}
                    disabled={ruleConditionData.length === 1}
                  >
                    <Cross />
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        <div>
          <Button
            size="small"
            onClick={addNewCondition}
            disabled={ruleConditionData.length >= MAX_RULE_CONDITIONS}
            customType="link"
          >
            Add a condition
          </Button>
        </div>
      </RuleConsoleItems>
    );
  }

  return null;
};

const RuleConsole = ({
  onSubmit,
  currentStep,
  setCurrentStep,
}: {
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  onSubmit: (
    values: EditRuleValuesType,
    formikHelpers: FormikHelpers<EditRuleValuesType>
  ) => void | Promise<any>;
}) => {
  const dispatch = useDispatch();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { editRuleId } = useSelector((store: RootState) => store.ruleEngine);

  const { data, isFetching } = useGetRuleByIdQuery(
    { entityId, groupId, ruleId: editRuleId },
    { skip: !groupId || !entityId || !editRuleId }
  );

  const { data: allConditions } = useGetAllConditionsQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );

  const {
    uuid,
    category,
    conditions = [],
    name,
    start_date,
    end_date,
    merchant_data,
    classification_data = [],
    apply_to_future_txns,
    apply_on_all_past_txns,
    priority,
  } = data || {};

  const ruleConditionData = conditions.map(
    ({ condition_operand, condition_operator, uuid, value }) => ({
      uuid,
      id: randomBytes(10).toString("hex"),
      field: condition_operand.uuid,
      conditionFunction: condition_operator.uuid,
      conditionValue: value,
    })
  );

  const classificationIds = classification_data.map((cls) => cls.uuid);

  let transactionsFor = "";
  if (apply_to_future_txns && !apply_on_all_past_txns) {
    transactionsFor = "allFutureTransactions";
  } else if (apply_to_future_txns && apply_on_all_past_txns) {
    transactionsFor = "allTransactions";
  }

  const onClose = () => {
    dispatch(closeEditModal());
  };

  if (isFetching) {
    return (
      <Modal.Content size="large" useCustomOverlay>
        <Loader />
      </Modal.Content>
    );
  }

  return (
    <Formik
      key={uuid}
      initialValues={{
        name: name || "",
        startDate: start_date ? dayjs(start_date).format(MM_DD_YYYY) : "",
        endDate: end_date ? dayjs(end_date).format(MM_DD_YYYY) : "",
        transactionsFor,
        assigned_category_id: category?.uuid || "",
        apply_for_future_txns: apply_to_future_txns || false,
        apply_for_txns_with_no_category: true,
        apply_on_all_past_txns: apply_on_all_past_txns || false,
        ruleConditionData,
        assigned_vendor_id: merchant_data?.uuid || "",
        classification_ids: classificationIds,
        priority: priority || "P2",
      }}
      validateOnChange
      validationSchema={editCategorisationRuleSchema(allConditions)}
      onSubmit={onSubmit}
    >
      {({
        submitForm,
        isSubmitting,
        values: {
          assigned_category_id,
          assigned_vendor_id,
          classification_ids,
        },
      }) => {
        const hasAtLeastOneAction =
          Boolean(assigned_category_id) ||
          Boolean(assigned_vendor_id) ||
          (classification_ids && classification_ids.length > 0);

        const isButtonDisabled = isSubmitting || !hasAtLeastOneAction;

        return (
          <Form>
            <Modal.Content size="xl" useCustomOverlay>
              <Modal.Header className="sm:t-pl-4">
                <div>
                  <Modal.Title>Edit rule</Modal.Title>
                  <Modal.Subtitle>
                    <RuleStepper
                      currentStep={currentStep}
                      onStepperClick={setCurrentStep}
                    />
                  </Modal.Subtitle>
                </div>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="sm:t-px-4">
                <div className="t-flex t-flex-col t-gap-6">
                  <TitleAndDuration />
                  <Divider />
                  <Conditions />
                  <Actions
                    assigned_category_id={assigned_category_id}
                    assigned_vendor_id={assigned_vendor_id}
                  />
                </div>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Button onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <ConditionalToolTip
                  condition={
                    !hasAtLeastOneAction &&
                    "Please select at least one of Action"
                  }
                >
                  <div>
                    <Button
                      customType="primary"
                      onClick={submitForm}
                      isLoading={isSubmitting}
                      disabled={isButtonDisabled}
                    >
                      Save & Preview transactions
                    </Button>
                  </div>
                </ConditionalToolTip>
              </Modal.FooterButtonGroup>
            </Modal.Content>
          </Form>
        );
      }}
    </Formik>
  );
};

export const EditRuleModal = () => {
  const dispatch = useDispatch();
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [currentStep, setCurrentStep] = useState(1);
  const [applyRule] = useApplyRuleMutation();
  const [editRule] = useEditRuleMutation();

  const { editRuleId, isEditModalOpen } = useSelector(
    (store: RootState) => store.ruleEngine
  );

  const { data } = useGetRuleByIdQuery(
    { entityId, groupId, ruleId: editRuleId },
    { skip: !groupId || !entityId || !editRuleId }
  );

  const { uuid: ruleId } = data || {};

  const onClose = () => {
    setCurrentStep(1);
    dispatch(closeEditModal());
  };

  const onActive = async ({ ruleId }: { ruleId: string }) => {
    try {
      await applyRule({ entityId, groupId, ruleId }).unwrap();
      successToast({ message: "Rule Changed" });
      onClose();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const onEdit = async (
    {
      endDate,
      startDate,
      ruleConditionData,
      assigned_category_id,
      assigned_vendor_id,
      classification_ids,
      name,
      priority,
      apply_for_future_txns,
      apply_on_all_past_txns,
      apply_for_txns_with_no_category,
    }: EditRuleValuesType,
    { resetForm }: FormikHelpers<EditRuleValuesType>
  ) => {
    if (!ruleId) return;

    try {
      const rule_condition_data = ruleConditionData.map(
        ({ conditionFunction, conditionValue, field, uuid }) => {
          if (Boolean(uuid)) {
            return {
              uuid,
              value: conditionValue,
              operator_id: conditionFunction,
              operand_id: field,
            };
          } else {
            return {
              value: conditionValue,
              operator_id: conditionFunction,
              operand_id: field,
            };
          }
        }
      );

      await editRule({
        entityId,
        groupId,
        rule_end_date: endDate ? dayjs(endDate).format(YYYY_MM_DD) : "",
        rule_start_date: startDate ? dayjs(startDate).format(YYYY_MM_DD) : "",
        rule_condition_data,
        assigned_category_id,
        assigned_merchant_id: assigned_vendor_id,
        assigned_classfication_ids: classification_ids,
        priority,
        apply_for_future_txns,
        apply_on_all_past_txns,
        apply_for_txns_with_no_category,
        name,
        ruleId,
      }).unwrap();
      setCurrentStep(2);
      resetForm();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Modal.Root open={isEditModalOpen} onOpenChange={onClose} modal={false}>
      {currentStep === 1 ? (
        <RuleConsole
          onSubmit={onEdit}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
      ) : (
        <TransactionsPreview
          uuid={ruleId}
          onSubmit={onActive}
          close={onClose}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          linfoLabel={
            <div className="t-flex t-w-full t-border-solid t-border t-border-blue-70 t-rounded t-px-2 t-py-1.5 t-items-center t-gap-1 t-bg-blue-20">
              <span className="t-text-blue-80">
                <InfoSolid size="24" />
              </span>
              <span className="t-text-body-sm t-text-text-60">
                Note: This won't affect manually categorised transactions.
              </span>
            </div>
          }
          submitText="Apply"
          modalTitle="Edit rule"
        />
      )}
    </Modal.Root>
  );
};
